/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { Component } from '@appCanvasCommon/component-system/Component';
import { ComponentInterface } from '@appCanvasCommon/interfaces/component.interface';
import { CanvasAngularLand } from '../../../../app-data/interfaces/canvas-angular-land';
import { AbstractApplication } from '@appCanvasCommon/core/AbstractApplication';
import { IExportContext } from '@appCanvasCommon/export/IExportContext';
import { CrewSizeApplication } from '../../../crew-sizes/CrewSizeApplication';
import { Rectangle } from 'pixi.js-legacy';
import { IConnectedCamera } from '../../../../app-data/interfaces/connected-camera.interface';

export class CrewSizeCapturer extends Component implements ComponentInterface {
	/**
	 * Invoked for each tile captured.
	 * @param canvasAngularLand
	 * @param application
	 * @param context
	 */
	public onExportTile(
		canvasAngularLand: CanvasAngularLand,
		application: AbstractApplication,
		context: IExportContext
	): void {
		const crewSizeApplication: CrewSizeApplication | null = canvasAngularLand.crewSizeService.application;
		const camera: IConnectedCamera | null = canvasAngularLand.cameraService.camera;

		// We only capture the crew sizes for the first row
		if (
			!context.isFirstRow ||
			!crewSizeApplication ||
			!camera ||
			!canvasAngularLand.crewSizeService.isVisible()
		) {
			return;
		}

		// If this is a new column in the grid, enable tiling mode so we can stitch crew sizes together cleanly
		if (!context.isFirstTileInRow) {
			crewSizeApplication.enableTilingMode();
		}

		// Create a capture rect with CrewSizeApplication screen space
		const captureRect: Rectangle = new Rectangle(
			camera.realToScreenX(context.tileWorldX),
			0,
			context.tileWidth,
			crewSizeApplication.height
		);
		const offsetX: number = context.tileX * context.tileWidth;
		crewSizeApplication.forceUpdate();
		canvasAngularLand.crewSizeService.markAsDirty();
		canvasAngularLand.crewSizeService.captureTile(captureRect, { x: offsetX, y: 0 });
	}
	/**
	 * Invoked once the export is finished.
	 * @param canvasAngularLand
	 * @param application
	 */
	public onExportComplete(canvasAngularLand: CanvasAngularLand, application: AbstractApplication): void {
		const crewSizeApplication: CrewSizeApplication | null = canvasAngularLand.crewSizeService.application;
		crewSizeApplication?.disableTilingMode();
		crewSizeApplication?.exporter?.purge();
	}
}
